/**
 * Estado puro de "prontidão para navegar" — decide que ação mostrar quando o
 * utilizador ainda não está no ponto de partida da rota. Sem React, sem
 * Google Maps: apenas geometria e um pequeno dicionário de rótulos.
 */

export type LatLng = { lat: number; lng: number };

export type NavigationReadinessInput = {
  /** Localização atual do utilizador, se conhecida. */
  userPosition?: LatLng | null;
  /** Ponto de partida real da rota (primeira paragem ou origem definida). */
  startPoint?: LatLng | null;
  /** Falso quando o botão/serviço de navegação do Google não está disponível. */
  googleNavAvailable?: boolean;
  /** Precisão do GPS em metros, quando disponível. */
  accuracyMeters?: number | null;
};

export type NavigationReadinessStatus =
  | "no_start_point"
  | "awaiting_location"
  | "far_from_start"
  | "ready_to_start"
  | "navigation_unavailable";

export type NavigationReadinessState = {
  status: NavigationReadinessStatus;
  distanceMeters: number | null;
};

/** Distância (m) a partir da qual consideramos o utilizador "no local". */
export const CLOSE_ENOUGH_METERS = 60;

function haversineMeters(a: LatLng, b: LatLng): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

export function computeNavigationReadiness(input: NavigationReadinessInput): NavigationReadinessState {
  if (!input.startPoint) return { status: "no_start_point", distanceMeters: null };
  if (!input.userPosition) return { status: "awaiting_location", distanceMeters: null };

  const distanceMeters = haversineMeters(input.userPosition, input.startPoint);
  // Absence of the Google navigation button/service must never surface as an
  // app error: once we know the distance, degrade gracefully to an
  // informative state instead of blocking the user.
  if (input.googleNavAvailable === false) {
    return { status: "navigation_unavailable", distanceMeters };
  }
  if (distanceMeters <= CLOSE_ENOUGH_METERS) {
    return { status: "ready_to_start", distanceMeters };
  }
  return { status: "far_from_start", distanceMeters };
}

export type NavigationReadinessLabels = {
  /** Título/mensagem informativa para o estado atual. */
  message: string;
  /** Ação primária disponível, ou null quando nenhuma se aplica. */
  primaryAction: string | null;
  /** Ação secundária disponível, ou null quando nenhuma se aplica. */
  secondaryAction: string | null;
};

type LabelDict = {
  navigateToStart: string;
  previewRoute: string;
  startRoute: string;
  awaitingLocation: string;
  noStartPoint: string;
  navigationUnavailable: string;
  farFromStart: (distance: string) => string;
};

const DICTS: Record<string, LabelDict> = {
  pt: {
    navigateToStart: "Navegar até ao ponto de partida",
    previewRoute: "Pré-visualizar percurso",
    startRoute: "Iniciar percurso",
    awaitingLocation: "A aguardar a sua localização…",
    noStartPoint: "Ainda sem ponto de partida definido.",
    navigationUnavailable: "Navegação por Google Maps indisponível de momento. Pode continuar a ver o percurso no mapa.",
    farFromStart: (d) => `Está a ${d} do ponto de partida.`,
  },
  en: {
    navigateToStart: "Navigate to the starting point",
    previewRoute: "Preview route",
    startRoute: "Start route",
    awaitingLocation: "Waiting for your location…",
    noStartPoint: "No starting point set yet.",
    navigationUnavailable: "Google Maps navigation is unavailable right now. You can still preview the route on the map.",
    farFromStart: (d) => `You are ${d} from the starting point.`,
  },
  es: {
    navigateToStart: "Navegar hasta el punto de partida",
    previewRoute: "Previsualizar recorrido",
    startRoute: "Iniciar recorrido",
    awaitingLocation: "Esperando su ubicación…",
    noStartPoint: "Aún no hay un punto de partida definido.",
    navigationUnavailable: "La navegación de Google Maps no está disponible ahora. Puede seguir viendo el recorrido en el mapa.",
    farFromStart: (d) => `Está a ${d} del punto de partida.`,
  },
  fr: {
    navigateToStart: "Naviguer jusqu'au point de départ",
    previewRoute: "Aperçu de l'itinéraire",
    startRoute: "Démarrer l'itinéraire",
    awaitingLocation: "En attente de votre position…",
    noStartPoint: "Aucun point de départ défini pour le moment.",
    navigationUnavailable: "La navigation Google Maps n'est pas disponible pour le moment. Vous pouvez toujours voir l'itinéraire sur la carte.",
    farFromStart: (d) => `Vous êtes à ${d} du point de départ.`,
  },
  it: {
    navigateToStart: "Naviga fino al punto di partenza",
    previewRoute: "Anteprima del percorso",
    startRoute: "Avvia percorso",
    awaitingLocation: "In attesa della tua posizione…",
    noStartPoint: "Nessun punto di partenza definito.",
    navigationUnavailable: "La navigazione di Google Maps non è disponibile ora. Puoi comunque vedere il percorso sulla mappa.",
    farFromStart: (d) => `Sei a ${d} dal punto di partenza.`,
  },
  de: {
    navigateToStart: "Zum Startpunkt navigieren",
    previewRoute: "Route vorschauen",
    startRoute: "Route starten",
    awaitingLocation: "Warte auf deinen Standort…",
    noStartPoint: "Noch kein Startpunkt festgelegt.",
    navigationUnavailable: "Google Maps-Navigation ist derzeit nicht verfügbar. Du kannst die Route weiterhin auf der Karte ansehen.",
    farFromStart: (d) => `Du bist ${d} vom Startpunkt entfernt.`,
  },
};

function dictFor(lang?: string | null): LabelDict {
  const code = (lang ?? "pt").slice(0, 2).toLowerCase();
  return DICTS[code] ?? DICTS.pt;
}

function formatDistance(meters: number, lang?: string | null): string {
  const value = meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(1)} km`;
  return value;
}

/** Traduz o estado puro em rótulos prontos a apresentar na UI. */
export function getNavigationReadinessLabels(
  state: NavigationReadinessState,
  lang?: string | null,
): NavigationReadinessLabels {
  const d = dictFor(lang);
  switch (state.status) {
    case "no_start_point":
      return { message: d.noStartPoint, primaryAction: null, secondaryAction: null };
    case "awaiting_location":
      return { message: d.awaitingLocation, primaryAction: null, secondaryAction: d.previewRoute };
    case "far_from_start":
      return {
        message: d.farFromStart(formatDistance(state.distanceMeters ?? 0, lang)),
        primaryAction: d.navigateToStart,
        secondaryAction: d.previewRoute,
      };
    case "ready_to_start":
      return { message: "", primaryAction: d.startRoute, secondaryAction: d.previewRoute };
    case "navigation_unavailable":
      return { message: d.navigationUnavailable, primaryAction: null, secondaryAction: d.previewRoute };
    default:
      return { message: "", primaryAction: null, secondaryAction: null };
  }
}
